import { Typography } from "antd";
import StatisticCard from "../StatisticCard";
import { useMemo } from "react";
import { formatPercentage } from "../../utils/calculations";
// import { ExpenseDetail, ExpenseTypeStatistic } from "../../types";
import { useGetExpenseTypes } from "../../hooks/useReports";
import { red } from "@ant-design/colors";
import { ExpenseType } from "@/types/expenseType";
import {
  ExpensesSummary,
  ExpenseTypeDetail,
  ExpenseTypeStatistic,
} from "@/types/report";

interface ExpensesOverviewProps {
  expenses?: ExpensesSummary;
  totalSales?: number;
  onExpenseTypeSelect: (expenseType: ExpenseType) => void;
}

const ExpensesOverview = ({
  expenses,
  totalSales = 0,
  onExpenseTypeSelect,
}: ExpensesOverviewProps) => {
  const { data: expenseTypes } = useGetExpenseTypes();

  const expensesMarginPercentage = useMemo(() => {
    if (totalSales === 0) {
      return "100%";
    }

    if (!expenses?.total_amount) {
      return "0%";
    }

    const totalExpenses = expenses.total_amount;
    const expenseMargin = (totalExpenses / totalSales) * 100;

    return formatPercentage(expenseMargin);
  }, [expenses, totalSales]);

  const expensesTypeStatistics: ExpenseTypeStatistic[] = useMemo(() => {
    if (!expenses || !expenseTypes) {
      return [];
    }

    const statistics = expenseTypes.map((et: ExpenseType) => {
      const expenseDetail = expenses?.details.find(
        (ed: ExpenseTypeDetail) => ed.type_id === et.id
      );

      if (!expenseDetail) {
        return {
          type_id: et.id,
          type_name: et.name,
          count: 0,
          total_amount: 0,
          percentage: "0%",
          expense_type: et,
        };
      }

      const margin = (expenseDetail.total_amount / expenses.total_amount) * 100;

      return {
        ...expenseDetail,
        percentage: formatPercentage(margin),
        expense_type: et,
      };
    });

    const sortedStatistics = statistics.sort(
      (a, b) => b.total_amount - a.total_amount
    );

    return sortedStatistics;
  }, [expenses, expenseTypes]);

  const percentageColor =
    totalSales < (expenses?.total_amount || 0) ? red[5] : undefined;

  return (
    <>
      <div style={{ gridColumn: "1 / -1" }}>
        <Typography.Title
          level={5}
          style={{ fontWeight: 500, marginBottom: 0 }}
        >
          Expenses Overview
        </Typography.Title>
      </div>

      <StatisticCard
        type="total"
        title="Total Expenses"
        value={expenses?.total_amount || 0}
        percentage={expensesMarginPercentage}
        percentageColor={percentageColor}
      />
      {expensesTypeStatistics.map((report) => (
        <StatisticCard
          key={report.type_id}
          title={report.type_name}
          value={report.total_amount}
          percentage={report.percentage}
          onClick={() => onExpenseTypeSelect(report.expense_type)}
        />
      ))}
    </>
  );
};

export default ExpensesOverview;
